import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ColorBadge } from '@/components/ColorBadge';

export function ProductAudit({ productId: initialProductId, onClose }) {
  const [productId, setProductId] = useState(initialProductId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [incomingDocs, setIncomingDocs] = useState([]);
  const [outgoingDocs, setOutgoingDocs] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [integrity, setIntegrity] = useState({ ok: true, diffs: [] });
  const [syncing, setSyncing] = useState(false);

  const loadData = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [prodRes, invRes, entradasRes, salidasRes, ordersRes] = await Promise.all([
        supabase.from('products').select('*').eq('id', id).maybeSingle(),
        supabase.from('inventory').select('size, color, quantity, pending_orders').eq('product_id', id),
        supabase
          .from('documents')
          .select('id, number, date, document_items!inner(id, product_id, size, color, quantity)')
          .eq('type', 'entrada')
          .eq('status', 'completed')
          .eq('document_items.product_id', id)
          .order('date', { ascending: true }),
        supabase
          .from('documents')
          .select('id, number, date, document_items!inner(id, product_id, size, color, quantity)')
          .eq('type', 'salida')
          .eq('status', 'completed')
          .eq('document_items.product_id', id)
          .order('date', { ascending: true }),
        supabase
          .from('order_items')
          .select('order_id, size, color, quantity, pending_quantity, delivered_quantity, preparation_quantity, orders!inner(id, number, status)')
          .eq('product_id', id)
      ]);

      if (prodRes.error) throw prodRes.error;
      if (invRes.error) throw invRes.error;
      if (entradasRes.error) throw entradasRes.error;
      if (salidasRes.error) throw salidasRes.error;
      if (ordersRes.error) throw ordersRes.error;

      setProduct(prodRes.data || null);
      setInventoryRows(invRes.data || []);
      setIncomingDocs((entradasRes.data || []).map(d => ({...d, type: 'entrada'})));
      setOutgoingDocs((salidasRes.data || []).map(d => ({...d, type: 'salida'})));
      setOrderItems(ordersRes.data || []);

      // Calcular diferencias inventory vs order_items activos
      const invMap = new Map();
      (invRes.data || []).forEach(r => {
        invMap.set(`${r.size}::${r.color}`, { invPending: Number(r.pending_orders) || 0 });
      });
      const activeItems = (ordersRes.data || []).filter(i => i.orders?.status === 'pending' || i.orders?.status === 'processing');
      const ordMap = new Map();
      activeItems.forEach(i => {
        const key = `${i.size}::${i.color}`;
        ordMap.set(key, (ordMap.get(key) || 0) + (Number(i.pending_quantity) || 0));
      });
      const diffs = [];
      const keys = new Set([...invMap.keys(), ...ordMap.keys()]);
      keys.forEach(k => {
        const inv = invMap.get(k)?.invPending || 0;
        const ord = ordMap.get(k) || 0;
        if (inv !== ord) {
          const [size, color] = k.split('::');
          diffs.push({ size, color, invPending: inv, orderPending: ord, delta: ord - inv });
        }
      });
      setIntegrity({ ok: diffs.length === 0, diffs });
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialProductId) loadData(initialProductId);
  }, [initialProductId]);

  const totals = useMemo(() => {
    const invQty = (inventoryRows || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const invPend = (inventoryRows || []).reduce((s, r) => s + (Number(r.pending_orders) || 0), 0);
    const entradas = (incomingDocs || []).reduce((s, d) => s + d.document_items.reduce((x, i) => x + (Number(i.quantity) || 0), 0), 0);
    const salidas = (outgoingDocs || []).reduce((s, d) => s + d.document_items.reduce((x, i) => x + (Number(i.quantity) || 0), 0), 0);
    // Solo líneas con pendiente > 0, sin filtrar por estado de cabecera
    const orderPendAll = (orderItems || []).reduce((s, i) => s + (Number(i.pending_quantity) || 0), 0);
    return { invQty, invPend, entradas, salidas, orderPendAll };
  }, [inventoryRows, incomingDocs, outgoingDocs, orderItems]);

  const getColorLabel = (rawColor) => {
    const c = String(rawColor ?? '').trim();
    if (!product || !Array.isArray(product.colors) || product.colors.length === 0) return c;
    // Heurística: si es un índice numérico, mapear a product.colors (0/1-based)
    const idx = Number(c);
    if (Number.isFinite(idx)) {
      if (idx >= 0 && idx < product.colors.length) return product.colors[idx];
      if (idx - 1 >= 0 && idx - 1 < product.colors.length) return product.colors[idx - 1];
    }
    return c;
  };

  const rowsByColor = useMemo(() => {
    const map = new Map();
    const add = (colorId, sizeKey, updater) => {
      const group = map.get(colorId) || { colorId, totalStock: 0, totalPendingInv: 0, totalEntradas: 0, totalSalidas: 0, totalOrdersPending: 0, sizes: new Map() };
      const sizeRow = group.sizes.get(sizeKey) || { size: sizeKey, stock: 0, pendingInv: 0, entradas: 0, salidas: 0, ordersPending: 0 };
      const updated = updater(sizeRow);
      group.sizes.set(sizeKey, updated);
      group.totalStock += updated.stock - (sizeRow.stock || 0);
      group.totalPendingInv += updated.pendingInv - (sizeRow.pendingInv || 0);
      group.totalEntradas += updated.entradas - (sizeRow.entradas || 0);
      group.totalSalidas += updated.salidas - (sizeRow.salidas || 0);
      group.totalOrdersPending += updated.ordersPending - (sizeRow.ordersPending || 0);
      map.set(colorId, group);
    };
    (inventoryRows || []).forEach(r => {
      const colorId = String(r.color);
      add(colorId, String(r.size), cur => ({...cur, stock: (Number(r.quantity) || 0), pendingInv: (Number(r.pending_orders) || 0), entradas: cur.entradas, salidas: cur.salidas, ordersPending: cur.ordersPending }));
    });
    (incomingDocs || []).forEach(d => d.document_items.forEach(i => {
      const colorId = String(i.color);
      add(colorId, String(i.size), cur => ({...cur, stock: cur.stock, pendingInv: cur.pendingInv, entradas: cur.entradas + (Number(i.quantity) || 0), salidas: cur.salidas, ordersPending: cur.ordersPending }));
    }));
    (outgoingDocs || []).forEach(d => d.document_items.forEach(i => {
      const colorId = String(i.color);
      add(colorId, String(i.size), cur => ({...cur, stock: cur.stock, pendingInv: cur.pendingInv, entradas: cur.entradas, salidas: cur.salidas + (Number(i.quantity) || 0), ordersPending: cur.ordersPending }));
    }));
    // Agregar pendientes por líneas (sin filtrar por estado de cabecera)
    (orderItems || []).forEach(i => {
      const colorId = String(i.color);
      add(colorId, String(i.size), cur => ({...cur, stock: cur.stock, pendingInv: cur.pendingInv, entradas: cur.entradas, salidas: cur.salidas, ordersPending: cur.ordersPending + (Number(i.pending_quantity) || 0) }));
    });
    const result = Array.from(map.values())
      .map(g => ({
        colorId: g.colorId,
        totalStock: g.totalStock,
        totalPendingInv: g.totalPendingInv,
        totalEntradas: g.totalEntradas,
        totalSalidas: g.totalSalidas,
        totalOrdersPending: g.totalOrdersPending,
        sizes: Array.from(g.sizes.values()).sort((a,b) => String(a.size).localeCompare(String(b.size)))
      }))
      .sort((a,b) => String(a.colorId).localeCompare(String(b.colorId)));
    return result;
  }, [inventoryRows, incomingDocs, outgoingDocs, orderItems, product]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Auditoría de Producto</h3>
        <div className="flex items-center gap-2">
          {!integrity.ok && (
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  setSyncing(true);
                  // Preparar updates por combinación para reemplazar pending_orders = orderPending
                  const updates = integrity.diffs.map(d => ({
                    product_id: product.id,
                    size: d.size,
                    color: d.color,
                    pending_orders: d.orderPending
                  }));
                  const { updateInventoryPendingOrders } = await import('@/lib/api/inventory');
                  await updateInventoryPendingOrders(updates, 'replace');
                  await loadData(product.id);
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing}
              title="Reemplazar pending_orders con la suma de líneas activas"
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar pendientes'}
            </Button>
          )}
          {onClose && (
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-sm text-gray-600">ID Producto (UUID)</label>
          <Input value={productId} onChange={e => setProductId(e.target.value)} placeholder="uuid del producto" />
        </div>
        <Button onClick={() => loadData(productId)} disabled={!productId || loading}>{loading ? 'Cargando...' : 'Cargar'}</Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded">{error}</div>
      )}

      {product && (
        <div className="bg-white border rounded p-3">
          <div className="font-medium">{product.code} - {product.description}</div>
          <div className="text-sm text-gray-600">Tallas: {(product.sizes||[]).join(', ')} · Colores: {(product.colors||[]).join(', ')}</div>
          {!integrity.ok ? (
            <div className="mt-2 text-xs text-red-700">Diferencias detectadas entre inventory.pending_orders y líneas activas. Revisa detalle por color/talla o pulsa “Sincronizar pendientes”.</div>
          ) : (
            <div className="mt-2 text-xs text-green-700">Pendientes en inventario alineados con líneas activas.</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border rounded p-3 text-center">
          <div className="text-xs text-gray-600">Stock (inventory)</div>
          <div className="text-2xl font-bold text-green-700">{totals.invQty}</div>
        </div>
        <div className="bg-white border rounded p-3 text-center">
          <div className="text-xs text-gray-600">Pedidos Pendientes (inventory)</div>
          <div className="text-2xl font-bold text-blue-700">{totals.invPend}</div>
        </div>
        <div className="bg-white border rounded p-3 text-center">
          <div className="text-xs text-gray-600">Entradas (docs)</div>
          <div className="text-2xl font-bold text-indigo-700">{totals.entradas}</div>
        </div>
        <div className="bg-white border rounded p-3 text-center">
          <div className="text-xs text-gray-600">Salidas (docs)</div>
          <div className="text-2xl font-bold text-orange-700">{totals.salidas}</div>
        </div>
        <div className="bg-white border rounded p-3 text-center">
          <div className="text-xs text-gray-600">Pendientes (líneas de pedido)</div>
          <div className="text-2xl font-bold text-red-700">{totals.orderPendAll}</div>
        </div>
      </div>

      <div className="bg-white border rounded">
        <div className="px-3 py-2 border-b font-medium">Detalle agrupado por color → tallas</div>
        <div className="divide-y">
          {rowsByColor.map((g, gi) => (
            <div key={gi} className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">Color: <ColorBadge color={g.colorId} /></div>
                <div className="text-xs text-gray-600 flex gap-4">
                  <span>Stock: <b className="text-green-700">{g.totalStock}</b></span>
                  <span>Inv. Pend.: <b className="text-blue-700">{g.totalPendingInv}</b></span>
                  <span>Entradas: <b>{g.totalEntradas}</b></span>
                  <span>Salidas: <b>{g.totalSalidas}</b></span>
                  <span>Orders Pend.: <b className="text-red-700">{g.totalOrdersPending}</b></span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2">Talla</th>
                      <th className="text-right px-3 py-2">Stock</th>
                      <th className="text-right px-3 py-2">Pend. (inv)</th>
                      <th className="text-right px-3 py-2">Entradas</th>
                      <th className="text-right px-3 py-2">Salidas</th>
                      <th className="text-right px-3 py-2">Pend. (orders)</th>
                      <th className="text-right px-3 py-2">Falta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.sizes.map((r, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{r.size}</td>
                        <td className="px-3 py-2 text-right text-green-700 font-medium">{r.stock}</td>
                        <td className="px-3 py-2 text-right text-blue-700">{r.pendingInv}</td>
                        <td className="px-3 py-2 text-right">{r.entradas}</td>
                        <td className="px-3 py-2 text-right">{r.salidas}</td>
                        <td className="px-3 py-2 text-right text-red-700">{r.ordersPending}</td>
                        <td className={`px-3 py-2 text-right font-bold ${((r.ordersPending || 0) - (r.stock || 0)) > 0 ? 'text-red-800' : 'text-gray-600'}`}>{Math.max(0, (r.ordersPending || 0) - (r.stock || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded">
          <div className="px-3 py-2 border-b font-medium">Entradas del producto</div>
          <div className="max-h-64 overflow-auto divide-y">
            {(incomingDocs || []).map((d) => (
              <div key={d.id} className="px-3 py-2 text-sm">
                <div className="font-medium">Entrada #{d.number} · {d.date}</div>
                {(d.document_items || []).map((i) => (
                  <div key={i.id} className="flex justify-between text-xs text-gray-700">
                    <span>{i.size} · {i.color}</span>
                    <span>+{i.quantity}</span>
                  </div>
                ))}
              </div>
            ))}
            {(!incomingDocs || incomingDocs.length === 0) && (
              <div className="px-3 py-2 text-sm text-gray-500">Sin entradas para este producto</div>
            )}
          </div>
        </div>
        <div className="bg-white border rounded">
          <div className="px-3 py-2 border-b font-medium">Salidas del producto</div>
          <div className="max-h-64 overflow-auto divide-y">
            {(outgoingDocs || []).map((d) => (
              <div key={d.id} className="px-3 py-2 text-sm">
                <div className="font-medium">Salida #{d.number} · {d.date}</div>
                {(d.document_items || []).map((i) => (
                  <div key={i.id} className="flex justify-between text-xs text-gray-700">
                    <span>{i.size} · {i.color}</span>
                    <span>-{i.quantity}</span>
                  </div>
                ))}
              </div>
            ))}
            {(!outgoingDocs || outgoingDocs.length === 0) && (
              <div className="px-3 py-2 text-sm text-gray-500">Sin salidas para este producto</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border rounded">
        <div className="px-3 py-2 border-b font-medium">Pedidos con líneas pendientes del producto</div>
        <div className="px-3 py-2 text-xs text-gray-600">
          Resumen por color (suma de pending_quantity): {
            (() => {
              const colorTotals = new Map();
              (orderItems || []).forEach(i => {
                const c = getColorLabel(i.color);
                colorTotals.set(c, (colorTotals.get(c) || 0) + (Number(i.pending_quantity) || 0));
              });
              const arr = Array.from(colorTotals.entries()).sort((a,b) => String(a[0]).localeCompare(String(b[0])));
              return arr.length === 0 ? 'Sin datos' : arr.map(([c, v]) => `${c}: ${v}`).join(' · ');
            })()
          }
        </div>
        <div className="max-h-72 overflow-auto divide-y">
          {(() => {
            const byOrder = new Map();
            // Agrupar por pedido todas las líneas con pendiente > 0 (sin filtrar por estado de cabecera)
            (orderItems || []).forEach((i) => {
              if (!byOrder.has(i.order_id)) byOrder.set(i.order_id, { order: i.orders, items: [] });
              byOrder.get(i.order_id).items.push(i);
            });
            const groups = Array.from(byOrder.values());
            if (groups.length === 0) return (<div className="px-3 py-2 text-sm text-gray-500">Sin pedidos pendientes para este producto</div>);
            return groups.map((g, idx) => {
              // Agrupar por color dentro del pedido
              const colorMap = new Map();
              g.items.forEach(i => {
                const c = getColorLabel(i.color);
                const list = colorMap.get(c) || [];
                list.push(i);
                colorMap.set(c, list);
              });
              const colors = Array.from(colorMap.entries()).sort((a,b) => String(a[0]).localeCompare(String(b[0])));
              return (
                <div key={idx} className="px-3 py-2 text-sm">
                  <div className="font-medium">Pedido #{g.order?.number} · Estado: {g.order?.status}</div>
                  <div className="mt-1 space-y-2">
                    {colors.map(([c, items]) => {
                      const totalColorPending = items.reduce((s, i) => s + (Number(i.pending_quantity) || 0), 0);
                      const sizeRows = items
                        .slice()
                        .sort((a,b) => String(a.size).localeCompare(String(b.size)));
                      return (
                        <div key={c} className="border rounded p-2">
                          <div className="text-xs text-gray-700 mb-1">Color: <b>{c}</b> · Pendiente total: <b className="text-red-700">{totalColorPending}</b></div>
                          <div className="space-y-1">
                            {sizeRows.map((i) => (
                              <div key={`${i.order_id}-${i.size}-${i.color}`} className="flex justify-between text-xs text-gray-700">
                                <span>Talla {i.size}</span>
                                <span>
                                  Pedido: <b>{i.quantity}</b> · Pendiente: <b className="text-red-700">{i.pending_quantity}</b> · Servido: {i.delivered_quantity || 0}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      <div className="bg-white border rounded">
        <div className="px-3 py-2 border-b font-medium">Pedidos donde aparece</div>
        <div className="max-h-64 overflow-auto divide-y">
          {(orderItems || []).map((i, idx) => (
            <div key={idx} className="px-3 py-2 flex justify-between text-sm">
              <div>
                <div className="font-medium">#{i.orders?.number}</div>
                <div className="text-gray-600">{i.size} · {i.color}</div>
              </div>
              <div className="text-right">
                <div>Qty: {i.quantity}</div>
                <div>Pendiente: <span className="font-semibold text-red-700">{i.pending_quantity}</span></div>
                <div>Servido: {i.delivered_quantity || 0} · Prep: {i.preparation_quantity || 0}</div>
                <div className="text-xs text-gray-500">Estado: {i.orders?.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


